import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { fetchClients } from "../../api/clientAPI";
import { fetchProjects } from "../../api/projectAPI";
import { fetchServices } from "../../api/servicesAPI";
import { fetchBlogs } from "../../api/blogsAPI";
import { fetchAnalyticsDashboard } from "../../api/analyticsAPI";

// ── Date range presets ────────────────────────────────────────
const PRESETS = [
  { label: "7 hari", days: 7 },
  { label: "14 hari", days: 14 },
  { label: "30 hari", days: 30 },
  { label: "90 hari", days: 90 },
  { label: "Custom", days: null },
];

function toDateStr(d) {
  return d.toISOString().split("T")[0];
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// ── Stat card ─────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, gradient, loading }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${gradient}`}>
      <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-2xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <span className="text-2xl">{icon}</span>
          {sub && <span className="text-xs font-overpass text-white/70 bg-white/10 px-2 py-0.5 rounded-full">{sub}</span>}
        </div>
        <div className="font-oswald text-3xl font-bold mb-0.5">
          {loading ? <span className="inline-block w-14 h-8 bg-white/20 rounded animate-pulse" /> : value}
        </div>
        <div className="text-xs font-overpass text-white/80 font-semibold uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

// ── DashboardC ────────────────────────────────────────────────
const ICONS = {
  clients: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M16 3.13a4 4 0 010 7.75M8 3.13a4 4 0 000 7.75" />
    </svg>
  ),
  projects: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  ),
  services: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  blogs: (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
};

const DashboardC = () => {
  // Content counts
  const [counts, setCounts] = useState({ clients: 0, projects: 0, services: 0, blogs: 0, comments: 0 });
  const [countsLoading, setCountsLoading] = useState(true);

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(null);

  // Date range
  const [preset, setPreset] = useState(0); // index into PRESETS
  const [customFrom, setCustomFrom] = useState(toDateStr(addDays(new Date(), -29)));
  const [customTo, setCustomTo]   = useState(toDateStr(new Date()));

  const getRange = useCallback(() => {
    const p = PRESETS[preset];
    if (p.days !== null) {
      return {
        from: toDateStr(addDays(new Date(), -(p.days - 1))),
        to:   toDateStr(new Date()),
      };
    }
    return { from: customFrom, to: customTo };
  }, [preset, customFrom, customTo]);

  // Load content counts
  useEffect(() => {
    (async () => {
      try {
        const [clients, projects, services, blogs] = await Promise.all([
          fetchClients().catch(() => []),
          fetchProjects().catch(() => []),
          fetchServices().catch(() => []),
          fetchBlogs().catch(() => []),
        ]);
        const totalComments = blogs.reduce((a, b) => a + (b.comments?.length || 0), 0);
        setCounts({ clients: clients.length, projects: projects.length, services: services.length, blogs: blogs.length, comments: totalComments });
      } finally {
        setCountsLoading(false);
      }
    })();
  }, []);

  // Load analytics
  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    const { from, to } = getRange();
    try {
      const data = await fetchAnalyticsDashboard(from, to);
      setAnalytics(data);
    } catch (err) {
      setAnalyticsError(err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [getRange]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const s = analytics?.summary || {};
  const daily = analytics?.daily_stats || [];
  const devices = analytics?.device_stats || [];
  const browsers = analytics?.browser_stats || [];
  const topPages = analytics?.top_pages || [];
  const topBlogsViewed = analytics?.top_blogs_viewed || [];

  const contentStats = [
    { key: "clients", label: "Total Clients", count: counts.clients, icon: ICONS.clients, color: "from-brand-500 to-brand-600", link: "/admin/client", bg: "bg-brand-50", textColor: "text-brand-700" },
    { key: "projects", label: "Total Projects", count: counts.projects, icon: ICONS.projects, color: "from-eco-500 to-eco-600", link: "/admin/project", bg: "bg-eco-50", textColor: "text-eco-700" },
    { key: "services", label: "Total Services", count: counts.services, icon: ICONS.services, color: "from-brand-600 to-eco-500", link: "/admin/service", bg: "bg-brand-50", textColor: "text-brand-700" },
    { key: "blogs", label: "Total Blogs", count: counts.blogs, icon: ICONS.blogs, color: "from-eco-500 to-brand-600", link: "/admin/blog", bg: "bg-eco-50", textColor: "text-eco-700" },
  ];

  return (
    <div className="flex-1 min-h-screen bg-slate-50 overflow-y-auto">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">

        {/* ── Welcome Banner ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 via-brand-700 to-eco-600 text-white p-8 lg:p-10 mb-8 shadow-strong"
        >
          <div className="absolute -top-10 -right-10 w-60 h-60 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-white/10 rounded-full blur-3xl" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-white/20 backdrop-blur-md border border-white/30 mb-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              Admin Panel
            </span>
            <h1 className="font-oswald text-3xl lg:text-4xl font-bold mb-2">
              Selamat Datang Kembali, Admin! 👋
            </h1>
            <p className="text-base text-white/90 font-overpass max-w-2xl leading-relaxed">
              Kelola konten website Defra Water Technology. Analytics selalu direkam — tidak pernah terhapus otomatis.
            </p>
          </div>
        </motion.div>

        {/* ── Content Stat Cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {contentStats.map((stat, idx) => (
            <motion.div key={stat.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.07 }}>
              <Link
                to={stat.link}
                className="group relative block bg-white rounded-2xl p-5 border border-slate-100 shadow-soft hover:shadow-medium card-hover no-underline hover:no-underline overflow-hidden"
              >
                <div className={`absolute -top-8 -right-8 w-24 h-24 rounded-full bg-gradient-to-br ${stat.color} opacity-10 group-hover:opacity-20 transition-opacity blur-2xl`} />
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat.color} text-white flex items-center justify-center mb-3 shadow-md`}>
                  {stat.icon}
                </div>
                <div className="text-xs font-overpass text-slate-500 mb-0.5">{stat.label}</div>
                <div className="font-oswald text-3xl font-bold text-slate-900 mb-2">
                  {countsLoading ? <span className="inline-block w-10 h-8 bg-slate-100 rounded animate-pulse" /> : stat.count}
                </div>
                <div className={`text-xs font-semibold ${stat.textColor} group-hover:gap-2 flex items-center gap-1 transition-all`}>
                  Kelola <span>→</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* ── Analytics Section ── */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-oswald text-xl font-semibold text-slate-900">Analytics Pengunjung</h2>
            <p className="text-xs text-slate-500 font-overpass mt-0.5">Data direkam selamanya — tidak ada penghapusan otomatis</p>
          </div>
          <button
            onClick={loadAnalytics}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700 transition-colors"
          >
            🔄 Refresh
          </button>
        </div>

        {/* Date range picker */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5 shadow-soft">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-overpass">Rentang:</span>
            {PRESETS.map((p, i) => (
              <button
                key={i}
                onClick={() => setPreset(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  preset === i
                    ? "bg-brand-600 text-white shadow-md shadow-brand-500/30"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {p.label}
              </button>
            ))}
            {PRESETS[preset].days === null && (
              <div className="flex items-center gap-2 ml-2">
                <input
                  type="date"
                  value={customFrom}
                  max={customTo}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 focus:border-brand-400 focus:ring-1 focus:ring-brand-100 outline-none"
                />
                <span className="text-slate-400 text-xs">—</span>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom}
                  max={toDateStr(new Date())}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 text-slate-700 focus:border-brand-400 focus:ring-1 focus:ring-brand-100 outline-none"
                />
              </div>
            )}
          </div>
        </div>

        {analyticsError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-5 text-sm text-red-700 font-overpass">
            ⚠️ Gagal memuat analytics: {analyticsError}. Pastikan Anda sudah login.
          </div>
        )}

        {/* Analytics summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          <StatCard icon="👁️" label="Views Hari Ini" value={s.today_views ?? "—"} gradient="bg-gradient-to-br from-blue-500 to-blue-600" loading={analyticsLoading} />
          <StatCard icon="🧑" label="Unik Hari Ini" value={s.today_unique ?? "—"} gradient="bg-gradient-to-br from-violet-500 to-violet-600" loading={analyticsLoading} />
          <StatCard icon="📈" label="Total Views" value={s.total_views ?? "—"} gradient="bg-gradient-to-br from-brand-500 to-brand-700" loading={analyticsLoading} />
          <StatCard icon="📊" label={`Views (${PRESETS[preset].label})`} value={s.range_views ?? "—"} gradient="bg-gradient-to-br from-eco-500 to-eco-700" loading={analyticsLoading} />
          <StatCard icon="💬" label="WA Clicks Hari Ini" value={s.wa_clicks_today ?? "—"} sub={`Total: ${s.wa_clicks_total ?? "—"}`} gradient="bg-gradient-to-br from-green-500 to-green-600" loading={analyticsLoading} />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
          {/* Daily views chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-oswald text-base font-semibold text-slate-900">Kunjungan Harian</h3>
                <p className="text-xs text-slate-500 font-overpass">{PRESETS[preset].label === "Custom" ? `${customFrom} – ${customTo}` : PRESETS[preset].label + " terakhir"}</p>
              </div>
              <div className="flex items-center gap-3 text-xs font-overpass">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-brand-500" /> Views</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-eco-500" /> Unik</span>
              </div>
            </div>
            {analyticsLoading ? (
              <div className="h-20 bg-slate-100 rounded-xl animate-pulse" />
            ) : daily.length === 0 ? (
              <p className="text-sm text-slate-400 font-overpass text-center py-6">Belum ada data untuk rentang ini</p>
            ) : (
              <div className="flex items-end gap-1 h-20">
                {daily.map((d, i) => {
                  const maxV = Math.max(...daily.map((x) => x.views), 1);
                  const maxU = Math.max(...daily.map((x) => x.unique_visitors), 1);
                  const hV = Math.max(4, (d.views / maxV) * 76);
                  const hU = Math.max(4, (d.unique_visitors / maxU) * 76);
                  const lbl = d.date?.slice(5) || "";
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group" title={`${d.date}: ${d.views} views, ${d.unique_visitors} unik`}>
                      <div className="w-full flex gap-px">
                        <div className="flex-1 rounded-sm" style={{ height: `${hV}px`, background: "#0284c7", opacity: 0.8 }} />
                        <div className="flex-1 rounded-sm" style={{ height: `${hU}px`, background: "#16a34a", opacity: 0.8 }} />
                      </div>
                      {daily.length <= 14 && (
                        <span className="text-[9px] text-slate-400 font-overpass leading-none">{lbl}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Device/Browser breakdown */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
            <h3 className="font-oswald text-base font-semibold text-slate-900 mb-4">Device & Browser</h3>
            {analyticsLoading ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => <div key={i} className="h-5 bg-slate-100 rounded animate-pulse" />)}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-overpass mb-2">Device</p>
                  <div className="space-y-1.5">
                    {devices.map((d) => {
                      const total = devices.reduce((a, b) => a + b.count, 0) || 1;
                      const pct = Math.round((d.count / total) * 100);
                      return (
                        <div key={d.device_type} className="flex items-center gap-2">
                          <span className="text-xs font-overpass text-slate-600 w-16 shrink-0">{d.device_type}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2">
                            <div className="h-2 rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-overpass mb-2">Browser</p>
                  <div className="space-y-1.5">
                    {browsers.slice(0, 4).map((b) => {
                      const total = browsers.reduce((a, c) => a + c.count, 0) || 1;
                      const pct = Math.round((b.count / total) * 100);
                      return (
                        <div key={b.browser} className="flex items-center gap-2">
                          <span className="text-xs font-overpass text-slate-600 w-16 shrink-0">{b.browser}</span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2">
                            <div className="h-2 rounded-full bg-eco-500" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top pages + Top blogs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
          {/* Top pages */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
            <h3 className="font-oswald text-base font-semibold text-slate-900 mb-4">Halaman Terpopuler</h3>
            {analyticsLoading ? (
              <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded-lg animate-pulse" />)}</div>
            ) : topPages.length === 0 ? (
              <p className="text-sm text-slate-400 font-overpass text-center py-4">Belum ada data</p>
            ) : (
              <div className="space-y-2">
                {topPages.slice(0, 8).map((p, i) => {
                  const max = topPages[0]?.views || 1;
                  const pct = Math.round((p.views / max) * 100);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-xs font-overpass text-slate-700 truncate">{p.page}</span>
                          <span className="text-xs font-semibold text-slate-900 shrink-0">{p.views}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100">
                          <div className="h-1.5 rounded-full bg-gradient-to-r from-brand-500 to-eco-500" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Blog activity + quick actions */}
          <div className="space-y-4">
            {/* Blog activity */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-oswald text-base font-semibold text-slate-900">Blog Terbanyak Dilihat</h3>
                <Link to="/admin/blog" className="text-xs font-semibold text-brand-600 hover:text-brand-700 no-underline">Kelola →</Link>
              </div>
              {analyticsLoading ? (
                <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-8 bg-slate-100 rounded-lg animate-pulse" />)}</div>
              ) : topBlogsViewed.length === 0 ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-4 rounded-xl bg-gradient-to-br from-brand-50 to-white border border-brand-100">
                    <div className="text-xs font-semibold uppercase tracking-wider text-brand-600 mb-1">Blog Posts</div>
                    <div className="font-oswald text-2xl font-bold text-slate-900">{countsLoading ? "—" : counts.blogs}</div>
                  </div>
                  <div className="p-4 rounded-xl bg-gradient-to-br from-eco-50 to-white border border-eco-100">
                    <div className="text-xs font-semibold uppercase tracking-wider text-eco-600 mb-1">Komentar</div>
                    <div className="font-oswald text-2xl font-bold text-slate-900">{countsLoading ? "—" : counts.comments}</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {topBlogsViewed.slice(0, 4).map((b, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                      <span className="text-xs font-overpass text-slate-600 truncate">{b.page?.replace("/blog/", "") || b.page}</span>
                      <span className="text-xs font-semibold text-brand-700 shrink-0 bg-brand-50 px-2 py-0.5 rounded-full">{b.views} views</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick actions */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-5 text-white shadow-strong relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-28 h-28 bg-brand-500/30 rounded-full blur-3xl" />
              <div className="relative">
                <h3 className="font-oswald text-base font-semibold mb-3">Quick Actions</h3>
                <div className="space-y-2">
                  <Link to="/admin/blog" className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-sm font-semibold no-underline hover:no-underline transition-colors">
                    ✍️ Buat Blog Baru <span>→</span>
                  </Link>
                  <Link to="/admin/project" className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-sm font-semibold no-underline hover:no-underline transition-colors">
                    📁 Tambah Project <span>→</span>
                  </Link>
                  <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-brand-500 to-eco-500 hover:opacity-90 text-sm font-semibold no-underline hover:no-underline transition-opacity">
                    🌐 Lihat Website <span>↗</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DashboardC;
